import React from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import { Helmet } from 'react-helmet';
import { LEGAL_DOCS } from '../data/legalDocs';

const ACCENT = '#137274';

/**
 * Standalone, directly-linkable privacy policy for the DSA Toolkit browser
 * extension (Chrome Web Store / Edge Add-ons require a real URL for this,
 * not just the in-app LegalModal).
 */
const DsaToolkitPrivacyPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const doc = LEGAL_DOCS.dsaToolkitPrivacy;
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark ? '#0b1112' : '#f6f9f9';

  return (
    <Box sx={{ minHeight: '100vh', background: pageBg }}>
      <Helmet>
        <title>DSA Toolkit — Privacy Policy | CrafterHack</title>
        <meta
          name="description"
          content="Privacy policy for the DSA Toolkit browser extension by CrafterHack."
        />
        <link rel="canonical" href="https://crafterhack.com/dsa-toolkit/privacy" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="DSA Toolkit — Privacy Policy" />
        <meta
          property="og:description"
          content="Privacy policy for the DSA Toolkit browser extension by CrafterHack."
        />
        <meta property="og:url" content="https://crafterhack.com/dsa-toolkit/privacy" />
        <meta property="og:image" content="https://crafterhack.com/dsa-toolkit-og.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://crafterhack.com/dsa-toolkit-og.png" />
      </Helmet>

      <Box
        sx={{
          maxWidth: 760,
          mx: 'auto',
          px: { xs: 2.5, sm: 4 },
          py: { xs: 6, sm: 9 },
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: { xs: '1.6rem', sm: '2rem' },
            letterSpacing: '-0.02em',
            mb: 0.75,
          }}
        >
          {doc.title}
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: muted, mb: 4 }}>
          Last updated {doc.updated}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3.5,
            bgcolor: isDark ? '#121a1c' : '#fff',
            border: `1px solid ${border}`,
            borderRadius: 3,
            p: { xs: 2.5, sm: 4 },
          }}
        >
          {doc.sections.map((section) => (
            <Box key={section.heading}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '1rem',
                  mb: 0.75,
                  letterSpacing: '-0.01em',
                  color: ACCENT,
                }}
              >
                {section.heading}
              </Typography>
              <Typography sx={{ color: muted, fontSize: '0.92rem', lineHeight: 1.7 }}>
                {section.body}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography sx={{ fontSize: '0.8rem', color: muted, mt: 4 }}>
          See also the{' '}
          <Box
            component="a"
            href="/dsa-toolkit"
            sx={{ color: ACCENT, fontWeight: 700, textDecoration: 'none' }}
          >
            DSA Toolkit
          </Box>{' '}
          extension page, or CrafterHack's own{' '}
          <Box
            component="a"
            href="/"
            sx={{ color: ACCENT, fontWeight: 700, textDecoration: 'none' }}
          >
            main Privacy Policy
          </Box>{' '}
          for the platform account, contests, and billing.
        </Typography>
      </Box>
    </Box>
  );
};

export default DsaToolkitPrivacyPage;
